import React, { useCallback, useEffect, useState } from 'react';

import { Helmet } from 'react-helmet';
import { Link, useLocation } from 'react-router-dom';

import { Box, Container, Tab, Tabs, Typography } from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentTenant from '~/hooks/useCurrentTenant';
import { usePromoOfferMetrics } from '~/hooks/usePromoOfferMetrics';

import { SyncPromoOfferMetricsParams } from '~/services/PromoService';

import isProd from '~/utils/isProd';

import { AddMockOfferMetricsFormData } from './AddMockOfferMetricsForm';
import AddMockOfferMetricsForm from './AddMockOfferMetricsForm';
import { OfferMetricsFormData } from './OfferMetricsForm';
import OfferMetricsTab from './OfferMetricsTab';
import PromoDisplayCheckForm from './PromoDisplayCheckForm';
import PromoDisplayCheckResults from './PromoDisplayCheckResults';
import SyncMetricsTab from './SyncMetricsTab';

// Type alias for the sortBy field to match the API contract
type SortByField = OfferMetricsFormData['sortBy'];

export const OFFER_METRICS_TABS = {
  OFFER_METRICS: 0,
  SYNC_OR_MOCK_METRICS: 1,
  PROMO_DISPLAY_CHECK: 2,
  PROMO_DISPLAY_CHECK_RESULTS: 3,
};

function OfferMetricsContainer() {
  const { push } = useAppNavigation();
  const location = useLocation();
  const { tenant } = useCurrentTenant();

  // Get tab from URL parameter, default to 0
  const urlParams = new URLSearchParams(location.search);
  const tabFromUrl = urlParams.get('tab');
  const initialTab = tabFromUrl ? parseInt(tabFromUrl, 10) : 0;

  // Offer Metrics State
  const [activeTab, setActiveTab] = useState(initialTab);
  const [offerMetricsPage, setOfferMetricsPage] = useState(0);
  const [offerMetricsPageSize, setOfferMetricsPageSize] = useState(10);
  const [offerMetricsFilters, setOfferMetricsFilters] = useState<OfferMetricsFormData>({
    offerId: '',
    region: 'AU',
    limit: 10,
    sortBy: 'updated_at',
    sortOrder: 'desc',
  });

  const {
    data: offerMetricsData,
    isLoading: offerMetricsLoading,
    totalCount: offerMetricsTotal,
    fetchOfferMetrics,
    addMockOfferMetricsData,
    syncOfferMetrics,
    isLoading: offerMockLoading,
  } = usePromoOfferMetrics();

  const handleOfferMetricsFormSubmit = useCallback(
    (formData: OfferMetricsFormData) => {
      setOfferMetricsFilters(formData);
      setOfferMetricsPage(0);
      fetchOfferMetrics({
        offerId: formData.offerId || undefined,
        region: formData.region,
        brand: tenant.brand,
        page: '0',
        limit: formData.limit.toString(),
        sortBy: formData.sortBy,
        sortOrder: formData.sortOrder,
      });
    },
    [fetchOfferMetrics],
  );

  const handleOfferMetricsPageChange = useCallback(
    (page: number, pageSize: number) => {
      setOfferMetricsPage(page);
      setOfferMetricsPageSize(pageSize);
      fetchOfferMetrics({
        offerId: offerMetricsFilters.offerId || undefined,
        region: offerMetricsFilters.region,
        brand: tenant.brand,
        page: page.toString(),
        limit: pageSize.toString(),
        sortBy: offerMetricsFilters.sortBy,
        sortOrder: offerMetricsFilters.sortOrder,
      });
    },
    [fetchOfferMetrics, offerMetricsFilters],
  );

  const handleOfferMetricsSortChange = useCallback(
    (sortBy: string, sortOrder: 'asc' | 'desc') => {
      setOfferMetricsFilters((prev) => ({
        ...prev,
        sortBy: sortBy as SortByField,
        sortOrder,
      }));
      setOfferMetricsPage(0); // Reset to first page when sorting changes
      fetchOfferMetrics({
        offerId: offerMetricsFilters.offerId || undefined,
        region: offerMetricsFilters.region,
        brand: tenant.brand,
        page: '0',
        limit: offerMetricsPageSize.toString(),
        sortBy: sortBy as SortByField,
        sortOrder,
      });
    },
    [fetchOfferMetrics, offerMetricsFilters, offerMetricsPageSize],
  );

  const handleTabChange = useCallback(
    (event: React.SyntheticEvent, newValue: number) => {
      setActiveTab(newValue);

      // Update URL with tab parameter
      const newUrlParams = new URLSearchParams(location.search);
      if (newValue === 0) {
        newUrlParams.delete('tab');
      } else {
        newUrlParams.set('tab', newValue.toString());
      }

      const newSearch = newUrlParams.toString();
      const newPath = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
      push(newPath);

      if (newValue === 0) {
        fetchOfferMetrics({
          offerId: offerMetricsFilters.offerId || undefined,
          region: offerMetricsFilters.region,
          brand: tenant.brand,
          page: offerMetricsPage.toString(),
          limit: offerMetricsPageSize.toString(),
          sortBy: offerMetricsFilters.sortBy,
          sortOrder: offerMetricsFilters.sortOrder,
        });
      }
    },
    [fetchOfferMetrics, offerMetricsFilters, offerMetricsPage, offerMetricsPageSize, push, location, tenant.brand],
  );

  const handleAddMockDataFormSubmit = useCallback(
    (formData: AddMockOfferMetricsFormData) => {
      addMockOfferMetricsData({
        mockOfferMetrics: [
          {
            offer_id: formData.offerId,
            mpv: formData.mpv,
            region: formData.region,
            brand: formData.brand,
            total_views_7d: formData.totalViews7d,
            conversion_rate_7d: formData.conversionRate7d,
            mpv_no_promo_7d: formData.mpvNoPromo7d,
            conversion_no_promo_7d: formData.conversionNoPromo7d,
            conversion_promo_7d: formData.conversionPromo7d,
            total_orders_7d: formData.totalOrders7d,
            total_margin_7d: formData.totalMargin7d,
            total_sales_7d: formData.totalSales7d,
            total_orders_no_promo_7d: formData.totalOrdersNoPromo7d,
            total_orders_promo_7d: formData.totalOrdersPromo7d,
            margin_no_promo_7d: formData.marginNoPromo7d,
            margin_diff_7d: formData.marginDiff7d,
          },
        ],
      });
    },
    [addMockOfferMetricsData],
  );

  const handleSyncMetricsFormSubmit = useCallback(
    (formData: SyncPromoOfferMetricsParams) => {
      const syncParams = {
        totalMaxLimit: formData.totalMaxLimit,
        startAt: formData.startAt,
        region: formData.region,
        chunkSize: formData.chunkSize,
      };

      syncOfferMetrics(syncParams);
    },
    [syncOfferMetrics],
  );

  // Handle URL changes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tabFromUrl = urlParams.get('tab');
    const newTab = tabFromUrl ? parseInt(tabFromUrl, 10) : 0;

    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [location.search, activeTab]);

  useEffect(() => {
    fetchOfferMetrics({
      offerId: offerMetricsFilters.offerId || undefined,
      region: offerMetricsFilters.region,
      brand: tenant.brand,
      page: offerMetricsPage.toString(),
      limit: offerMetricsPageSize.toString(),
      sortBy: offerMetricsFilters.sortBy,
      sortOrder: offerMetricsFilters.sortOrder,
    });
  }, [tenant.brand]);

  const isProduction = isProd();

  return (
    <Container maxWidth={false}>
      <Helmet>
        <title>Offer Metrics</title>
      </Helmet>
      <PageSubheader title="Offer Metrics" />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Offer Metrics" />
          {isProduction ? <Tab label="Sync Metrics" /> : <Tab label="Mock Data" />}
          <Tab label="Promo Display Check" />
          <Tab label="Promo Display Check Results" />
        </Tabs>
      </Box>

      {activeTab === OFFER_METRICS_TABS.OFFER_METRICS && (
        <OfferMetricsTab
          offerMetricsData={offerMetricsData}
          offerMetricsLoading={offerMetricsLoading}
          offerMetricsTotal={offerMetricsTotal}
          offerMetricsPage={offerMetricsPage}
          offerMetricsPageSize={offerMetricsPageSize}
          offerMetricsFilters={offerMetricsFilters}
          handleOfferMetricsFormSubmit={handleOfferMetricsFormSubmit}
          handleOfferMetricsPageChange={handleOfferMetricsPageChange}
          handleOfferMetricsSortChange={handleOfferMetricsSortChange}
        />
      )}

      {activeTab === OFFER_METRICS_TABS.SYNC_OR_MOCK_METRICS && isProduction && (
        <SyncMetricsTab
          offerMetricsLoading={offerMetricsLoading}
          handleSyncMetricsFormSubmit={handleSyncMetricsFormSubmit}
        />
      )}

      {activeTab === OFFER_METRICS_TABS.SYNC_OR_MOCK_METRICS && !isProduction && (
        <Box mt={4}>
          <Typography variant="h6" gutterBottom>
            Add Mock Offer Metrics Data
          </Typography>
          <Typography variant="body1" gutterBottom>
            This will add mock offer metrics data to the database (only available in test environments).
          </Typography>
          <AddMockOfferMetricsForm onSubmit={handleAddMockDataFormSubmit} isLoading={offerMockLoading} />
        </Box>
      )}

      {activeTab === OFFER_METRICS_TABS.PROMO_DISPLAY_CHECK && (
        <Box mt={4}>
          <Typography variant="h6" gutterBottom>
            Promo Display Check
          </Typography>
          <Typography variant="body1" gutterBottom>
            Check if a promo display configuration should be shown for a specific offer. See also{' '}
            <Link to="/marketing/offer-metrics">Offer Metrics</Link> for offers with specific metric.
          </Typography>
          <PromoDisplayCheckForm />
        </Box>
      )}

      {activeTab === OFFER_METRICS_TABS.PROMO_DISPLAY_CHECK_RESULTS && (
        <Box mt={4}>
          <PromoDisplayCheckResults />
        </Box>
      )}
    </Container>
  );
}

export default OfferMetricsContainer;
